import { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { toDate, isWithinRange, getMonthBounds } from '../utils/weekHelpers';
import logger from '../../../../utils/logger';
import { toast } from 'react-toastify';

/**
 * Fetches all collections once, caches them, and re-filters client-side
 * when the week changes. Avoids re-fetching 6 full collections on every
 * week navigation click.
 *
 * @param {Date} weekStart - Monday 00:00
 * @param {Date} weekEnd   - Sunday 23:59
 * @returns {{ loading, data }}
 */
export default function useWeeklyReportData(weekStart, weekEnd) {
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState(null);
  const hasFetched = useRef(false);

  // Fetch collections once
  useEffect(() => {
    if (hasFetched.current) return;
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      try {
        const [
          txnSnap,
          invSnap,
          jobSnap,
          paySnap,
          custSnap,
          vehSnap,
        ] = await Promise.all([
          getDocs(collection(db, 'transactions')),
          getDocs(collection(db, 'newInvoices')),
          getDocs(collection(db, 'jobs')),
          getDocs(collection(db, 'payables')),
          getDocs(collection(db, 'customers')),
          getDocs(collection(db, 'vehicles')),
        ]);

        if (cancelled) return;
        hasFetched.current = true;

        // --- Raw arrays ---
        const allTransactions = txnSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const allInvoices = invSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const allJobs = jobSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const allPayables = paySnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // --- Lookup maps (use null-prototype objects to avoid prototype pollution) ---
        const customersByName = Object.create(null);
        const customersById = Object.create(null);
        custSnap.docs.forEach(d => {
          const c = { id: d.id, ...d.data() };
          customersById[d.id] = c;
          const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
          if (fullName) customersByName[fullName] = c;
        });

        const vehiclesById = Object.create(null);
        const vehiclesByReg = Object.create(null);
        vehSnap.docs.forEach(d => {
          const v = { id: d.id, ...d.data() };
          vehiclesById[d.id] = v;
          if (v.registrationNumber) vehiclesByReg[v.registrationNumber] = v;
        });

        // Payables that aren't fully paid (for aging)
        const openPayables = allPayables.filter(p => p.paymentStatus !== 'Fully Paid');

        // Jobs lookup by jobCardNo
        const jobsByNo = Object.create(null);
        allJobs.forEach(j => {
          if (j.jobCardNo) jobsByNo[j.jobCardNo] = j;
        });

        setRaw({
          allTransactions,
          allInvoices,
          allPayables,
          openPayables,
          customersByName,
          customersById,
          vehiclesById,
          vehiclesByReg,
          jobsByNo,
        });
      } catch (err) {
        logger.error('Failed to fetch weekly report data', err);
        toast.error('Failed to load weekly report data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, []); // fetch once on mount

  // Re-filter when week changes (pure client-side, no Firestore reads)
  const data = useMemo(() => {
    if (!raw) return null;

    const { allTransactions, allInvoices } = raw;

    const weekTransactions = allTransactions.filter(t => {
      const d = toDate(t.transactionDate) || toDate(t.createdAt);
      return isWithinRange(d, weekStart, weekEnd);
    });

    const preWeekTransactions = allTransactions.filter(t => {
      const d = toDate(t.transactionDate) || toDate(t.createdAt);
      return d && d < weekStart;
    });

    const weekInvoices = allInvoices.filter(inv => {
      const d = toDate(inv.invoiceDate) || toDate(inv.datePosted);
      return isWithinRange(d, weekStart, weekEnd);
    });

    // Month-scoped invoices for budget review & summary
    const { monthStart, monthEnd } = getMonthBounds(weekStart);
    const monthInvoices = allInvoices.filter(inv => {
      const d = toDate(inv.invoiceDate) || toDate(inv.datePosted);
      return isWithinRange(d, monthStart, monthEnd);
    });

    return {
      ...raw,
      weekTransactions,
      preWeekTransactions,
      weekInvoices,
      monthInvoices,
      monthStart,
      monthEnd,
    };
  }, [raw, weekStart, weekEnd]);

  return { loading, data };
}
